import React, { useEffect, useState } from 'react';
import { orderService } from '../../services/api';
import { Order, OrderStats } from '../../types/order';
import styles from './Dashboard.module.css';

type BurndownPoint = {
  label: string;
  ideal: number;
  actual: number;
};

type DailyBar = {
  label: string;
  date: Date;
  created: number;
  completed: number;
};

const isCompletedStatus = (status: string): boolean => status.toUpperCase() === 'CONCLUIDA';

const parseInputDateAsLocal = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const parseApiDate = (value: string): Date => {
  // LocalDateTime do backend vem sem timezone; forca parse em horario local do navegador.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(value)) {
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes, secondsWithMs] = timePart.split(':');
    const [seconds, milliseconds = '0'] = secondsWithMs.split('.');
    return new Date(
      year,
      month - 1,
      day,
      Number(hours),
      Number(minutes),
      Number(seconds),
      Number(milliseconds.padEnd(3, '0').slice(0, 3)),
    );
  }

  return new Date(value);
};

const formatInputDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const applyPresetRange = (preset: '7d' | '14d' | '30d' | 'currentMonth'): { start: string; end: string } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(today);

  if (preset === 'currentMonth') {
    start.setDate(1);
  } else if (preset === '30d') {
    start.setDate(today.getDate() - 29);
  } else if (preset === '14d') {
    start.setDate(today.getDate() - 13);
  } else {
    start.setDate(today.getDate() - 6);
  }

  return {
    start: formatInputDate(start),
    end: formatInputDate(today),
  };
};

const buildBurndownData = (orders: Order[], startDateValue: string, endDateValue: string): BurndownPoint[] => {
  if (!orders.length) return [];

  const timelineStart = parseInputDateAsLocal(startDateValue);
  timelineStart.setHours(0, 0, 0, 0);

  const timelineEnd = parseInputDateAsLocal(endDateValue);
  timelineEnd.setHours(23, 59, 59, 999);

  if (Number.isNaN(timelineStart.getTime()) || Number.isNaN(timelineEnd.getTime()) || timelineStart > timelineEnd) {
    return [];
  }

  const periodDays = Math.max(
    1,
    Math.ceil((timelineEnd.getTime() - timelineStart.getTime() + 1) / (1000 * 60 * 60 * 24)),
  );

  const startBefore = new Date(timelineStart);
  startBefore.setMilliseconds(-1);

  const carryoverOrders = orders.filter((order) => {
    const createdAt = parseApiDate(order.createdAt);
    if (createdAt > startBefore) return false;

    if (!isCompletedStatus(order.status)) return true;

    const updatedAt = parseApiDate(order.updatedAt);
    return updatedAt > startBefore;
  });

  const periodOrders = orders.filter((order) => {
    const createdAt = parseApiDate(order.createdAt);
    return createdAt >= timelineStart && createdAt <= timelineEnd;
  });

  const totalScope = carryoverOrders.length + periodOrders.length;

  const isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const timelineDates = Array.from({ length: periodDays }, (_, index) => {
    const date = new Date(timelineStart);
    date.setDate(timelineStart.getDate() + index);
    date.setHours(23, 59, 59, 999);
    return date;
  });

  const totalWorkdays = timelineDates.filter((date) => !isWeekend(date)).length;

  const points: BurndownPoint[] = [];
  let workdayIndex = 0;

  for (let i = 0; i < periodDays; i += 1) {
    const date = timelineDates[i];

    const periodCreatedUntilDate = periodOrders.filter((order) => parseApiDate(order.createdAt) <= date).length;
    const carryoverCompletedUntilDate = carryoverOrders.filter(
      (order) => isCompletedStatus(order.status) && parseApiDate(order.updatedAt) <= date,
    ).length;
    const periodCompletedUntilDate = periodOrders.filter(
      (order) => isCompletedStatus(order.status) && parseApiDate(order.updatedAt) <= date,
    ).length;

    // A linha ideal avanca apenas em dias uteis; finais de semana ficam "achatados".
    const effectiveWorkdayIndex = isWeekend(date) ? Math.max(0, workdayIndex - 1) : workdayIndex;
    const progressRatio =
      totalWorkdays <= 1
        ? (totalWorkdays === 0 ? 0 : (isWeekend(date) ? 0 : 1))
        : effectiveWorkdayIndex / (totalWorkdays - 1);
    const ideal = Math.max(0, Math.round(totalScope * (1 - progressRatio)));
    const actual = Math.max(0, carryoverOrders.length + periodCreatedUntilDate - carryoverCompletedUntilDate - periodCompletedUntilDate);

    points.push({
      label: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
      ideal,
      actual,
    });

    if (!isWeekend(date)) {
      workdayIndex += 1;
    }
  }

  return points;
};

const buildLinePath = (values: number[], width: number, height: number, maxValue: number): string => {
  if (!values.length) return '';

  const stepX = values.length > 1 ? width / (values.length - 1) : width;

  return values
    .map((value, index) => {
      const x = index * stepX;
      const y = maxValue === 0 ? height : height - (value / maxValue) * height;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
};

const buildDailyBars = (orders: Order[], startDateValue: string, endDateValue: string): DailyBar[] => {
  const start = parseInputDateAsLocal(startDateValue);
  start.setHours(0, 0, 0, 0);

  const end = parseInputDateAsLocal(endDateValue);
  end.setHours(23, 59, 59, 999);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return [];
  }

  const days = Math.ceil((end.getTime() - start.getTime() + 1) / (1000 * 60 * 60 * 24));
  const bars: DailyBar[] = [];

  for (let i = 0; i < days; i += 1) {
    const dayStart = new Date(start);
    dayStart.setDate(start.getDate() + i);
    dayStart.setHours(0, 0, 0, 0);

    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);

    const created = orders.filter((order) => {
      const createdAt = parseApiDate(order.createdAt);
      return createdAt >= dayStart && createdAt <= dayEnd;
    }).length;

    const completed = orders.filter((order) => {
      if (!isCompletedStatus(order.status)) return false;
      const updatedAt = parseApiDate(order.updatedAt);
      return updatedAt >= dayStart && updatedAt <= dayEnd;
    }).length;

    bars.push({
      label: dayStart.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
      date: dayStart,
      created,
      completed,
    });
  }

  return bars;
};

const InfoIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={styles.infoIconSvg}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const CardInfo: React.FC<{ text: string }> = ({ text }) => (
  <span className={styles.infoWrap}>
    <InfoIcon />
    <span className={styles.tooltip} role="tooltip">{text}</span>
  </span>
);

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<OrderStats>({ total: 0, pending: 0, completed: 0, overdue: 0 });
  const [orders, setOrders] = useState<Order[]>([]);
  const [burndownData, setBurndownData] = useState<BurndownPoint[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<'7d' | '14d' | '30d' | 'currentMonth' | null>('30d');
  const [dateError, setDateError] = useState('');
  const [fetchError, setFetchError] = useState('');
  const [loading, setLoading] = useState(true);
  const [dailyBars, setDailyBars] = useState<DailyBar[]>([]);

  useEffect(() => {
    const defaultRange = applyPresetRange('30d');
    setStartDate(defaultRange.start);
    setEndDate(defaultRange.end);

    Promise.all([
      orderService.getStats(defaultRange.start, defaultRange.end),
      orderService.getAll({ perPage: '300' }),
    ])
      .then(([statsResponse, ordersResponse]) => {
        setStats(statsResponse);
        setOrders(ordersResponse);
        setFetchError('');
      })
      .catch(() => {
        setFetchError('Erro ao carregar dados do servidor. Verifique sua conexao.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!startDate || !endDate) return;
    orderService.getStats(startDate, endDate)
      .then(setStats)
      .catch(() => {});
  }, [startDate, endDate]);

  useEffect(() => {
    if (!startDate || !endDate || !orders.length) return;

    if (parseInputDateAsLocal(startDate) > parseInputDateAsLocal(endDate)) {
      setDateError('A data inicial não pode ser maior que a data final.');
      setBurndownData([]);
      return;
    }

    setDateError('');
    setBurndownData(buildBurndownData(orders, startDate, endDate));
    setDailyBars(buildDailyBars(orders, startDate, endDate));
  }, [orders, startDate, endDate]);

  const minChartWidth = 600;
  const chartHeight = 250;
  const chartLabelArea = 30;
  const stepX = burndownData.length > 1 ? 50 : 0;
  const chartWidth = Math.max(minChartWidth, (burndownData.length - 1) * stepX + 40);
  const chartTotalHeight = chartHeight + chartLabelArea;
  const values = burndownData.flatMap((point) => [point.ideal, point.actual]);
  const maxValue = Math.max(...values, 0);
  const idealPath = buildLinePath(
    burndownData.map((point) => point.ideal),
    chartWidth,
    chartHeight,
    maxValue,
  );
  const actualPath = buildLinePath(
    burndownData.map((point) => point.actual),
    chartWidth,
    chartHeight,
    maxValue,
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Dashboard</h1>
        <p className={styles.subtitle}>
          Bem-vindo ao painel administrativo do sistema de iluminação pública
        </p>
      </div>

      {fetchError && (
        <div className={styles.errorBanner}>
          <p>{fetchError}</p>
        </div>
      )}

      <div className={styles.summarySection}>
        <h4 className={styles.summaryTitle}>Resumo do Sistema</h4>
        <div className={styles.cardsGrid}>
          <div className={`${styles.card} ${styles.cardBlue}`}>
            <h5 className={styles.cardTitle}>Total de Ordens <CardInfo text="Total de ordens no período selecionado (abertas + concluídas + em atraso)." /></h5>
            <p className={`${styles.cardNumber} ${styles.cardNumberBlue}`}>{loading ? '...' : stats.total}</p>
            <p className={styles.cardDescription}>Ordens de serviço</p>
          </div>

          <div className={`${styles.card} ${styles.cardOrange}`}>
            <h5 className={styles.cardTitle}>Abertas <CardInfo text="Ordens aguardando execução: aberta, avulsa, em andamento e pendente." /></h5>
            <p className={`${styles.cardNumber} ${styles.cardNumberOrange}`}>{loading ? '...' : stats.pending}</p>
            <p className={styles.cardDescription}>Aguardando execução</p>
          </div>

          <div className={`${styles.card} ${styles.cardGreen}`}>
            <h5 className={styles.cardTitle}>Concluídas <CardInfo text="Ordens finalizadas com sucesso no período selecionado." /></h5>
            <p className={`${styles.cardNumber} ${styles.cardNumberGreen}`}>{loading ? '...' : stats.completed}</p>
            <p className={styles.cardDescription}>Finalizadas com sucesso</p>
          </div>

          <div className={`${styles.card} ${styles.cardRed}`}>
            <h5 className={styles.cardTitle}>Em Atraso <CardInfo text="Ordens fora do prazo de execução no período selecionado." /></h5>
            <p className={`${styles.cardNumber} ${styles.cardNumberRed}`}>{loading ? '...' : stats.overdue}</p>
            <p className={styles.cardDescription}>Fora do prazo</p>
          </div>
        </div>
      </div>

      <div className={styles.chartSection}>
        <h4 className={styles.summaryTitle}>Gráfico de Burndown</h4>
        <div className={styles.chartFilters}>
          <div className={styles.presetButtons}>
            <button
              type="button"
              className={`${styles.presetButton} ${selectedPreset === '7d' ? styles.presetButtonActive : ''}`}
              onClick={() => {
                const range = applyPresetRange('7d');
                setSelectedPreset('7d');
                setStartDate(range.start);
                setEndDate(range.end);
              }}
            >
              7 dias
            </button>
            <button
              type="button"
              className={`${styles.presetButton} ${selectedPreset === '14d' ? styles.presetButtonActive : ''}`}
              onClick={() => {
                const range = applyPresetRange('14d');
                setSelectedPreset('14d');
                setStartDate(range.start);
                setEndDate(range.end);
              }}
            >
              14 dias
            </button>
            <button
              type="button"
              className={`${styles.presetButton} ${selectedPreset === '30d' ? styles.presetButtonActive : ''}`}
              onClick={() => {
                const range = applyPresetRange('30d');
                setSelectedPreset('30d');
                setStartDate(range.start);
                setEndDate(range.end);
              }}
            >
              30 dias
            </button>
            <button
              type="button"
              className={`${styles.presetButton} ${selectedPreset === 'currentMonth' ? styles.presetButtonActive : ''}`}
              onClick={() => {
                const range = applyPresetRange('currentMonth');
                setSelectedPreset('currentMonth');
                setStartDate(range.start);
                setEndDate(range.end);
              }}
            >
              Mês atual
            </button>
          </div>

          <label className={styles.dateField}>
            Início
            <input
              type="date"
              className={styles.dateInput}
              value={startDate}
              max={endDate || undefined}
              onChange={(event) => {
                setSelectedPreset(null);
                setStartDate(event.target.value);
              }}
            />
          </label>

          <label className={styles.dateField}>
            Fim
            <input
              type="date"
              className={styles.dateInput}
              value={endDate}
              min={startDate || undefined}
              onChange={(event) => {
                setSelectedPreset(null);
                setEndDate(event.target.value);
              }}
            />
          </label>
        </div>

        {dateError && <p className={styles.dateError}>{dateError}</p>}

        {loading ? (
          <p className={styles.emptyChartText}>Carregando gráfico...</p>
        ) : burndownData.length === 0 ? (
          <p className={styles.emptyChartText}>Sem dados suficientes para gerar o burndown.</p>
        ) : (
          <>
            <div className={styles.chartLegend}>
              <span className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.legendIdeal}`} /> Linha ideal
              </span>
              <span className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.legendActual}`} /> Trabalho restante real
              </span>
            </div>

            <div className={styles.chartWrapper}>
               <svg
                 className={styles.chartSvg}
                 viewBox={`0 0 ${chartWidth} ${chartTotalHeight}`}
                 role="img"
                 aria-label="Gráfico de burndown com linha ideal e linha real"
               >
                <line x1="0" y1={chartHeight} x2={chartWidth} y2={chartHeight} className={styles.axisLine} />
                <line x1="0" y1="0" x2="0" y2={chartHeight} className={styles.axisLine} />

                <path d={idealPath} className={styles.idealLine} />
                <path d={actualPath} className={styles.actualLine} />

                {burndownData.map((point, idx) => {
                  const ptStepX = burndownData.length > 1 ? chartWidth / (burndownData.length - 1) : chartWidth;
                  const cx = idx * ptStepX;
                  const iy = maxValue === 0 ? chartHeight : chartHeight - (point.ideal / maxValue) * chartHeight;
                  const ay = maxValue === 0 ? chartHeight : chartHeight - (point.actual / maxValue) * chartHeight;
                  return (
                    <React.Fragment key={`dots-${point.label}-${point.actual}`}>
                      <circle cx={cx} cy={iy} r="4" className={styles.idealDot} />
                      <circle cx={cx} cy={ay} r="4" className={styles.actualDot} />
                    </React.Fragment>
                  );
                })}

                {burndownData.map((_point, idx) => {
                  const gridStepX = burndownData.length > 1 ? chartWidth / (burndownData.length - 1) : chartWidth;
                  const x = idx * gridStepX;
                  return (
                    <line
                      key={`grid-${idx}`}
                      x1={x}
                      y1={0}
                      x2={x}
                      y2={chartHeight}
                      className={styles.gridLine}
                    />
                  );
                })}

                {burndownData.map((point, idx) => {
                  const labelStepX = burndownData.length > 1 ? chartWidth / (burndownData.length - 1) : chartWidth;
                  const x = idx * labelStepX;
                  return (
                    <text
                      key={`label-${point.label}-${point.actual}`}
                      x={x}
                      y={chartTotalHeight - 4}
                      textAnchor="middle"
                      className={styles.xAxisLabel}
                    >
                      {point.label}
                    </text>
                  );
                })}
              </svg>
            </div>
          </>
        )}
      </div>

      {(() => {
        const barChartHeight = 220;
        const barLabelArea = 30;
        const barChartTotalHeight = barChartHeight + barLabelArea;
        const barWidth = 14;
        const barGap = 6;
        const sidePadding = 30;
        const barChartWidth = sidePadding + dailyBars.length * (barWidth + barGap) * 2 + sidePadding;

        const createdValues = dailyBars.map((d) => d.created);
        const completedValues = dailyBars.map((d) => d.completed);
        const barMax = Math.max(...createdValues, ...completedValues, 1);

        return (
          <div className={styles.chartSection}>
            <h4 className={styles.summaryTitle}>Ordens por Dia</h4>

            {loading ? (
              <p className={styles.emptyChartText}>Carregando gráfico...</p>
            ) : dailyBars.length === 0 ? (
              <p className={styles.emptyChartText}>Sem dados suficientes para gerar o gráfico.</p>
            ) : (
              <>
                <div className={styles.chartLegend}>
                  <span className={styles.legendItem}>
                    <span className={`${styles.legendDot} ${styles.legendCreated}`} /> Abertas
                  </span>
                  <span className={styles.legendItem}>
                    <span className={`${styles.legendDot} ${styles.legendCompleted}`} /> Concluídas
                  </span>
                </div>

                <div className={styles.chartWrapper}>
                   <svg
                     className={styles.chartSvg}
                     viewBox={`0 0 ${barChartWidth} ${barChartTotalHeight}`}
                     role="img"
                     aria-label="Gráfico de barras com ordens abertas e concluídas por dia"
                   >
                    <line x1="0" y1={barChartHeight} x2={barChartWidth} y2={barChartHeight} className={styles.axisLine} />
                    <line x1="0" y1="0" x2="0" y2={barChartHeight} className={styles.axisLine} />

                    {createdValues.map((value, idx) => {
                      const x = sidePadding + idx * (barWidth + barGap) * 2;
                      const barH = value === 0 ? 0 : Math.max(2, (value / barMax) * barChartHeight);
                      const y = barChartHeight - barH;
                      return (
                        <React.Fragment key={`created-${idx}`}>
                          <rect
                            x={x}
                            y={y}
                            width={barWidth}
                            height={barH}
                            className={styles.barCreated}
                          />
                          {value > 0 && (
                            <text
                              x={x + barWidth / 2}
                              y={barH > 16 ? y + 14 : y - 4}
                              textAnchor="middle"
                              className={styles.barValue}
                            >
                              {value}
                            </text>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {completedValues.map((value, idx) => {
                      const x = sidePadding + idx * (barWidth + barGap) * 2 + barWidth + barGap;
                      const barH = value === 0 ? 0 : Math.max(2, (value / barMax) * barChartHeight);
                      const y = barChartHeight - barH;
                      return (
                        <React.Fragment key={`completed-${idx}`}>
                          <rect
                            x={x}
                            y={y}
                            width={barWidth}
                            height={barH}
                            className={styles.barCompleted}
                          />
                          {value > 0 && (
                            <text
                              x={x + barWidth / 2}
                              y={barH > 16 ? y + 14 : y - 4}
                              textAnchor="middle"
                              className={styles.barValue}
                            >
                              {value}
                            </text>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {dailyBars.length > 1 &&
                      dailyBars.map((_bar, idx) => {
                        const x = sidePadding + idx * (barWidth + barGap) * 2 + barWidth + barGap / 2;
                        return (
                          <line
                            key={`grid-bar-${idx}`}
                            x1={x}
                            y1={0}
                            x2={x}
                            y2={barChartHeight}
                            className={styles.gridLine}
                          />
                        );
                      })}

                    {dailyBars.map((bar, idx) => {
                      const x = sidePadding + idx * (barWidth + barGap) * 2 + barWidth + barGap / 2;
                      return (
                        <text
                          key={`daily-label-${bar.label}-${bar.created}-${bar.completed}`}
                          x={x}
                          y={barChartTotalHeight - 4}
                          textAnchor="middle"
                          className={styles.xAxisLabel}
                        >
                          {bar.label}
                        </text>
                      );
                    })}
                  </svg>
                </div>
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default Dashboard;